import React, { useMemo } from 'react';
import ReactFlow, {
	Elements,
	ReactFlowProvider,
	Controls,
	MiniMap,
	OnLoadParams,
	Connection,
	Edge,
	ConnectionMode,
	Node,
} from 'react-flow-renderer';
import {
	Button,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Typography,
	Select,
	Link,
	SelectChangeEvent,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import {
	decoratedAttributes,
	StylovyzeDialog,
	useStyles as useThemeStyles,
	openDrawer,
} from '@innovyze/stylovyze';
import {
	ReactFlowSchema,
	RehabAction,
	NODE_TYPES,
	RehabTreeValidation,
	ReactFlowDecisionTree,
	isCustomCostAction,
} from '@Types';
import InnoInputNode from '@Components/ReactFlowNodes/InnoInputNode';
import ActionInputNode from '@Components/ReactFlowNodes/ActionInputNode';
import ButtonEdge, { ReadOnlyCustomEdge } from '@Components/ButtonEdge';
import CanvasIcon from '@Components/Tools/CanvasIcon.svg';
import { useStyles } from './DecisionTree.style';
import { useGlobalization } from '@Translations';
import { useDispatch } from 'react-redux';

interface TreeDashboardTabProps {
	elements: Elements;
	isDirty: boolean;
	isDisabled: boolean;
	isRehabTreeEditable: boolean;
	isRehabTreeRunning: boolean;
	isRehabTreeValidating: boolean;
	hasFullAccess: boolean;
	rehabActions: RehabAction[];
	rehabTreeValidationResult: RehabTreeValidation;
	nodeIdToEdit: string | undefined;
	setNodeIdToEdit: (id: string) => void;
	defectCodes: string[] | null;
	setDefectCodes: (codes: string[] | null) => void;
	addActionDialogOpen: boolean;
	setAddActionDialogOpen: (open: boolean) => void;
	addActionSelection: string;
	setAddActionSelection: (selection: string) => void;
	reactFlowInstance: OnLoadParams | null;
	setReactFlowInstance: (rfi: OnLoadParams) => void;
	onConnect: (params: Connection | Edge) => void;
	handleElementsRemove: (elements: Elements) => void;
	handleNodeDragStop: (event: React.MouseEvent, node: Node) => void;
	addToTree: (values: ReactFlowSchema) => void;
	setRehabActionToEdit: (action: RehabAction | undefined | null) => void;
	handleSaveTree: () => void;
	treeId: string;
	currentTree: ReactFlowDecisionTree;
	runRehabTreeHandler: (treeId: string) => void;
	validateRehabTreeHandler: (treeId: string) => void;
	addQueryHandler: () => void;
}

const RUN_REHAB_TREE_ADP_PROPS = {
	'adp-click': 'true',
	'adp-component-id': 'run-rehab-tree',
	'adp-component-type': 'button',
	'adp-component-name': 'Run Rehab Tree',
} as const;

export const TreeDashboardTab = ({
	elements,
	isDirty,
	isDisabled,
	isRehabTreeEditable,
	isRehabTreeRunning,
	isRehabTreeValidating,
	hasFullAccess,
	rehabActions,
	rehabTreeValidationResult,
	nodeIdToEdit,
	setNodeIdToEdit,
	defectCodes,
	setDefectCodes,
	addActionDialogOpen,
	setAddActionDialogOpen,
	addActionSelection,
	setAddActionSelection,
	reactFlowInstance,
	setReactFlowInstance,
	onConnect,
	handleElementsRemove,
	handleNodeDragStop,
	addToTree,
	setRehabActionToEdit,
	handleSaveTree,
	treeId,
	currentTree,
	runRehabTreeHandler,
	validateRehabTreeHandler,
	addQueryHandler,
}: TreeDashboardTabProps) => {
	const { t } = useGlobalization();
	const { themePalette } = useThemeStyles();
	const classes = useStyles(themePalette);
	const dispatch = useDispatch();

	const nodeTypes = {
		innoInput: InnoInputNode,
		actionInput: ActionInputNode,
	};

	const edgeTypes = hasFullAccess
		? { buttonedge: ButtonEdge }
		: { buttonedge: ReadOnlyCustomEdge };

	const style = { width: '100%', height: '100%' };

	const runAdpAttrs = decoratedAttributes({
		treeId,
		name: currentTree?.name ?? '',
		systemtype: currentTree?.systemType?.id ?? '',
		inspectionstandard: currentTree?.inspectionStandard ?? '',
		assettype: currentTree?.assetType?.id ?? '',
		nodecount: String(currentTree?.nodes?.length ?? 0),
		actioncount: String(rehabActions?.length ?? 0),
	});

	const onLoad = (rfi: OnLoadParams) => {
		setReactFlowInstance(rfi);
	};

	const addActionHandler = () => {
		setAddActionDialogOpen(true);
	};

	const addActionSelectChangeHandler = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		if (target == undefined) {
			setAddActionSelection('');
			setDefectCodes(null);
			return;
		}
		setAddActionSelection(target.value);
		const action = rehabActions.find(
			rehabaction => rehabaction._id == target.value,
		);
		if (action != null) {
			setDefectCodes(action.settings.defects);
		} else setDefectCodes(null);
	};

	const addActionDialogConfirmHandler = () => {
		setAddActionDialogOpen(false);

		if (addActionSelection != '') {
			if (addActionSelection === '-1') {
				setRehabActionToEdit(undefined);
				dispatch(openDrawer('addNewActionDrawer'));
			} else {
				let id = null;
				const node = elements.find(node => node.id == nodeIdToEdit);
				if (node != undefined) {
					id = node.id;
				}
				const action = rehabActions.find(
					rehabaction => rehabaction._id == addActionSelection,
				);
				const reactFlowItem: ReactFlowSchema = {
					id: id,
					parentId: null,
					type: NODE_TYPES.action,
					label: action?.actionId,
					data: {
						label: action?.actionId as string,
						nodeData: {
							actionDefectCodes: defectCodes ?? [],
							actionId: action?._id,
							treeId: action?.treeId,
							unit:
								action && isCustomCostAction(action)
									? action.unit
									: undefined,
							unitCost:
								action && isCustomCostAction(action)
									? action.unitCost
									: undefined,
							notes: action?.notes,
						},
					},
				};
				addToTree(reactFlowItem);
			}
		}

		setAddActionSelection('');
		setDefectCodes(null);
	};

	const ReactFlowCanvas = (
		<ReactFlowProvider>
			<div className="reactflow-wrapper">
				<ReactFlow
					nodesDraggable={isRehabTreeEditable}
					nodesConnectable={isRehabTreeEditable}
					elements={elements}
					onLoad={onLoad}
					onConnect={onConnect}
					onElementsRemove={handleElementsRemove}
					onNodeDragStop={handleNodeDragStop}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					connectionMode={ConnectionMode.Loose}
					hidden={elements.length === 0}
					style={style}>
					<Controls showInteractive={hasFullAccess} />
					<MiniMap
						nodeColor={node => {
							switch (node.type) {
								case 'input':
									return 'gold';
								case 'default':
									return '#00ff00';
								case 'output':
									return 'blue';
								default:
									return '#eee';
							}
						}}
						nodeStrokeWidth={1}
					/>
				</ReactFlow>
			</div>
		</ReactFlowProvider>
	);

	const PlaceholderForEmptyCanvas = (
		<div className={classes.canvasReactFlowEmpty}>
			<div className={classes.canvasReactFlowEmptyIcon}>
				<img src={CanvasIcon} alt={t('Decision Tree Icon')} />
			</div>
			<div className={classes.canvasReactFlowEmptyTitle}>
				<Typography gutterBottom variant="h6">
					{t('Your decision tree is empty')}
				</Typography>
			</div>

			{hasFullAccess ? (
				<div className={classes.canvasReactFlowEmptyNote}>
					<Typography align="center" gutterBottom variant="body1">
						{t('Use the "Add" buttons to begin')}
					</Typography>
					<Typography align="center" gutterBottom variant="body1">
						{t('constructing your tree.')}
					</Typography>
				</div>
			) : (
				[]
			)}

			{hasFullAccess ? (
				<div className={classes.canvasReactFlowEmptyActions}>
					<Button
						disabled={isDisabled}
						variant="contained"
						className={classes.canvasReactFlowEmptyActionsQuery}
						startIcon={<Add />}
						onClick={addQueryHandler}>
						{t('Query')}
					</Button>

					<Button
						disabled={isDisabled}
						variant="contained"
						startIcon={<Add />}
						onClick={addActionHandler}>
						{t('Action')}
					</Button>
				</div>
			) : (
				[]
			)}
			{ReactFlowCanvas}
		</div>
	);

	const addActionDialogContent = useMemo(() => {
		const node = elements.find(node => node.id == nodeIdToEdit);
		if (nodeIdToEdit != '' && node != undefined) {
			if (node.data.nodeData?.actionDefectCodes) {
				if (defectCodes == null) {
					setDefectCodes(node.data.nodeData.actionDefectCodes);
				}
			}
		}
		return (
			<Grid style={{ width: '300px' }}>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel id="action-type-id" variant="outlined">
						{t('Action Type')}
					</InputLabel>
					<Select
						labelId="action-type-id"
						value={addActionSelection}
						data-cy="actionChoiceList"
						name="action"
						onChange={addActionSelectChangeHandler}
						label={t('Action Type')}>
						{nodeIdToEdit == undefined || nodeIdToEdit == '' ? (
							<MenuItem key={'newAction'} value={'-1'}>
								{t('Add New Action')}
							</MenuItem>
						) : (
							[]
						)}
						{rehabActions?.map((action, index) => (
							<MenuItem
								key={index + '' + action._id}
								value={action._id}>
								{action.actionId}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
		);
	}, [rehabActions, addActionSelection, defectCodes, nodeIdToEdit]);

	return (
		<>
			<StylovyzeDialog
				dialogProps={{ maxWidth: 'lg' }}
				data-cy="add-action-dialog"
				open={addActionDialogOpen}
				title={
					nodeIdToEdit == undefined || nodeIdToEdit == ''
						? t('Add Action')
						: t('Edit Action')
				}
				content={addActionDialogContent}
				confirmText={
					nodeIdToEdit == undefined || nodeIdToEdit == ''
						? t('Add Action')
						: t('Save Action')
				}
				confirmButtonProps={{ color: 'primary' }}
				cancelText={t('Cancel')}
				onConfirm={addActionDialogConfirmHandler}
				onCancel={() => {
					setAddActionDialogOpen(false);
					setDefectCodes(null);
					setTimeout(() => {
						setNodeIdToEdit('');
					}, 500);
				}}
			/>
			{hasFullAccess ? (
				<div className={classes.treeActionsContainer}>
					<Grid container spacing={1} className={classes.treeActions}>
						<Grid item>
							<Button
								disabled={isDisabled}
								variant="outlined"
								startIcon={<Add />}
								onClick={addQueryHandler}>
								{t('Query')}
							</Button>
						</Grid>
						<Grid item>
							<Button
								disabled={isDisabled}
								variant="outlined"
								startIcon={<Add />}
								onClick={addActionHandler}>
								{t('Action')}
							</Button>
						</Grid>
					</Grid>

					<div className={classes.treeActionsRight}>
						{rehabTreeValidationResult.treeId === treeId &&
						!rehabTreeValidationResult.isValid ? (
							<Grid
								item
								style={{
									alignSelf: 'center',
									color: '#AA0000',
								}}>
								{t('Validation Failed: ')}
								<Link
									component="button"
									variant="body2"
									color="inherit"
									className={classes.errorsButton}
									onClick={() =>
										validateRehabTreeHandler(treeId)
									}>
									{t('View Errors')}
								</Link>
							</Grid>
						) : (
							<></>
						)}
						<Button
							variant="contained"
							className={classes.treeActionsRightRun}
							disabled={
								isDirty ||
								(rehabTreeValidationResult.treeId === treeId &&
									!rehabTreeValidationResult.isValid) ||
								currentTree?.nodes?.length === 0 ||
								isDisabled
							}
							data-cy="button-NewTree"
							color="primary"
							{...RUN_REHAB_TREE_ADP_PROPS}
							{...runAdpAttrs}
							onClick={() => runRehabTreeHandler(treeId)}>
							{isRehabTreeRunning ? t('Running') : t('Run')}
						</Button>
						<Button
							variant="outlined"
							className={classes.treeActionsRightSave}
							color="secondary"
							onClick={handleSaveTree}
							disabled={!isDirty || isDisabled}>
							{isRehabTreeValidating
								? t('Previewing')
								: t('Save')}
						</Button>
					</div>
				</div>
			) : (
				[]
			)}

			{elements.length == 0 && PlaceholderForEmptyCanvas}
			{elements.length > 0 && (
				<Grid
					container
					direction="column"
					alignItems="center"
					className={classes.canvasReactFlow}
					justifyContent="center">
					{ReactFlowCanvas}
				</Grid>
			)}
		</>
	);
};

export default TreeDashboardTab;
